import { MAX_LIVES, type TakeMeHomeSave } from '@/types/save'

export interface DailyReward {
  day: number
  coins: number
  gems: number
  lives?: number
  powerups?: Record<string, number>
}

export const DAILY_REWARDS: DailyReward[] = [
  { day: 1, coins: 10, gems: 0 },
  { day: 2, coins: 50, gems: 0 },
  { day: 3, coins: 100, gems: 1 },
  { day: 4, coins: 150, gems: 2 },
  { day: 5, coins: 200, gems: 3, lives: 1 },
  { day: 6, coins: 300, gems: 5 },
  { day: 7, coins: 500, gems: 10, lives: 2, powerups: { shield: 1 } },
]

export interface DailyStatus {
  canClaim: boolean
  nextDay: number
  streakDay: number
  reward: DailyReward
}

function dayKey(iso: string): number {
  if (!iso) return 0
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 0
  return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86400000)
}

function nowKey(now: number): number {
  const d = new Date(now)
  return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86400000)
}

export function computeDailyStatus(daily: { lastClaimIso: string; streakDay: number }, now: number): DailyStatus {
  const last = dayKey(daily.lastClaimIso)
  const today = nowKey(now)
  const diff = last === 0 ? Infinity : today - last

  if (diff <= 0) {
    const day = Math.max(1, Math.min(7, daily.streakDay))
    return { canClaim: false, nextDay: (daily.streakDay % 7) + 1, streakDay: daily.streakDay, reward: DAILY_REWARDS[day - 1] }
  }
  if (diff === 1) {
    const nextDay = (daily.streakDay % 7) + 1
    return { canClaim: true, nextDay, streakDay: daily.streakDay, reward: DAILY_REWARDS[nextDay - 1] }
  }
  return { canClaim: true, nextDay: 1, streakDay: 0, reward: DAILY_REWARDS[0] }
}

export function claimDaily(save: TakeMeHomeSave, now: number): TakeMeHomeSave {
  const status = computeDailyStatus(save.meta.daily, now)
  if (!status.canClaim) return save
  const reward = status.reward

  const powerups = { ...save.powerups }
  if (reward.powerups) {
    for (const [id, qty] of Object.entries(reward.powerups)) {
      powerups[id] = (powerups[id] ?? 0) + qty
    }
  }

  return {
    ...save,
    economy: {
      ...save.economy,
      coins: save.economy.coins + reward.coins,
      gems: save.economy.gems + reward.gems,
      lives: Math.min(MAX_LIVES, save.economy.lives + (reward.lives ?? 0)),
    },
    powerups,
    meta: {
      ...save.meta,
      daily: { lastClaimIso: new Date(now).toISOString(), streakDay: status.nextDay },
    },
    updatedAt: now,
  }
}
